'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ExponentCalculator() {
  const t = useTranslations("tools.exponentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [base, setBase] = useState('');
  const [exponent, setExponent] = useState('');
  const [result, setResult] = useState('');
  const [calculationResult, setCalculationResult] = useState<{
    expression: string;
    finalResult: string;
    scientificNotation?: string;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const parseInput = (value: string): number | null => {
    if (value === '' || value === null) return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) < 1e-10 && num !== 0) {
      return num.toExponential(10);
    }
    if (Math.abs(num) > 1e15 || (Math.abs(num) < 1e-4 && num !== 0)) {
      return num.toExponential(10);
    }
    if (Number.isInteger(num)) {
      return num.toString();
    }
    const str = num.toString();
    if (str.length > 15) {
      return num.toPrecision(12);
    }
    return num.toString();
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCalculationResult(null);

    const baseVal = parseInput(base);
    const exponentVal = parseInput(exponent);
    const resultVal = parseInput(result);

    const filledCount = [baseVal, exponentVal, resultVal].filter(v => v !== null).length;

    if (filledCount < 2) {
      setError(t('errorTwoValues'));
      return;
    }

    if (filledCount === 3) {
      setError(t('errorThreeValues'));
      return;
    }

    let calculatedValue: number;
    let expression: string;
    let finalResultValue: number;

    try {
      if (baseVal === null) {
        if (resultVal! <= 0 && exponentVal !== Math.floor(exponentVal!)) {
          setError(t('errorBaseCalculation'));
          return;
        }
        calculatedValue = Math.pow(resultVal!, 1 / exponentVal!);
        setBase(formatNumber(calculatedValue));
        expression = `${formatNumber(calculatedValue)}^${formatNumber(exponentVal!)}`;
        finalResultValue = resultVal!;
      } else if (exponentVal === null) {
        if (baseVal <= 0) {
          setError(t('errorExponentBase'));
          return;
        }
        if (resultVal! <= 0) {
          setError(t('errorExponentResult'));
          return;
        }
        calculatedValue = Math.log(resultVal!) / Math.log(baseVal);
        setExponent(formatNumber(calculatedValue));
        expression = `${formatNumber(baseVal)}^${formatNumber(calculatedValue)}`;
        finalResultValue = resultVal!;
      } else {
        if (baseVal === 0 && exponentVal <= 0) {
          setError(t('errorZeroBase'));
          return;
        }
        if (baseVal < 0 && exponentVal !== Math.floor(exponentVal)) {
          setError(t('errorNegativeBase'));
          return;
        }
        calculatedValue = Math.pow(baseVal, exponentVal);
        if (!isFinite(calculatedValue)) {
          setError(t('errorTooLarge'));
          return;
        }
        setResult(formatNumber(calculatedValue));
        expression = `${formatNumber(baseVal)}^${formatNumber(exponentVal)}`;
        finalResultValue = calculatedValue;
      }

      const scientificNotation = (Math.abs(finalResultValue) > 1e6 || (Math.abs(finalResultValue) < 1e-4 && finalResultValue !== 0))
        ? finalResultValue.toExponential(10)
        : undefined;

      setCalculationResult({
        expression,
        finalResult: formatNumber(finalResultValue),
        scientificNotation,
      });
    } catch (error) {
      setError(t('errorCalculation'));
    }
  };

  const handleClear = () => {
    setBase('');
    setExponent('');
    setResult('');
    setCalculationResult(null);
    setError('');
  };

  const handleUseE = (e: React.MouseEvent) => {
    e.preventDefault();
    setBase(Math.E.toString());
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' },
    { padding: '1.5rem' },
    { padding: '2rem', maxWidth: '600px' },
    { padding: '2.5rem', maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolDescriptionStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0' },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.75rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const exponentDisplayStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '2rem',
      flexWrap: 'wrap' as const
    },
    { flexDirection: 'column' as const, gap: '0.75rem' },
    { flexDirection: 'row' as const, gap: '1rem' },
    { flexDirection: 'row' as const, gap: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      transition: 'all 0.3s ease',
      fontWeight: 600,
    },
    { width: '100%', maxWidth: '200px', padding: '0.75rem', fontSize: '1.125rem' },
    { width: '120px', padding: '0.75rem', fontSize: '1.25rem' },
    { width: '120px', padding: '0.75rem', fontSize: '1.25rem' }
  );

  const operatorStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.5rem' },
    { fontSize: '2rem' },
    { fontSize: '2rem' }
  );

  const useLinkStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '1.5rem',
  };

  const linkStyle: React.CSSProperties = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.9rem',
    transition: 'color 0.3s ease',
    cursor: 'pointer',
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' as const },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
    { width: '100%', padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { width: 'auto', padding: '0.875rem 2.5rem', fontSize: '1.1rem' },
    { width: 'auto', padding: '0.875rem 2.5rem', fontSize: '1.1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    { width: '100%', padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { width: 'auto', padding: '0.875rem 2.5rem', fontSize: '1.1rem' },
    { width: 'auto', padding: '0.875rem 2.5rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #ef4444',
    color: '#991b1b',
    marginTop: '1rem',
    display: error ? 'block' : 'none',
  };

  const resultsStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #22c55e',
    display: calculationResult ? 'block' : 'none',
  };

  const resultsH3Style: React.CSSProperties = {
    color: '#166534',
    fontSize: '1.5rem',
    margin: '0 0 1.5rem 0',
    fontWeight: 700,
  };

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  };

  const resultLabelStyle: React.CSSProperties = {
    color: '#475569',
    fontWeight: 600,
    fontSize: '1rem',
  };

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem', wordBreak: 'break-all' as const },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const formulaBoxStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #7c3aed',
      margin: '1.5rem 0',
      fontFamily: "'Courier New', monospace",
      textAlign: 'center' as const,
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const formulaStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.2rem' }
  );

  const examplesGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const exampleCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #f59e0b',
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, marginLeft: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const referenceListStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const referenceItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem',
    paddingLeft: '1.5rem',
    position: 'relative' as const,
  };

  const referenceLinkStyle: React.CSSProperties = {
    color: '#2563eb',
    textDecoration: 'none',
    transition: 'color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={toolDescriptionStyle}>{t('toolDescription')}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={exponentDisplayStyle}>
              <div>
                <input
                  type="text"
                  value={base}
                  onChange={(e) => setBase(e.target.value)}
                  placeholder={t('basePlaceholder')}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>
              <span style={operatorStyle}>^</span>
              <div>
                <input
                  type="text"
                  value={exponent}
                  onChange={(e) => setExponent(e.target.value)}
                  placeholder={t('exponentPlaceholder')}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>
              <span style={operatorStyle}>=</span>
              <div>
                <input
                  type="text"
                  value={result}
                  onChange={(e) => setResult(e.target.value)}
                  placeholder={t('resultPlaceholder')}
                  style={inputStyle}
                  inputMode="decimal"
                />
              </div>
            </div>

            <div style={useLinkStyle}>
              <a href="#" onClick={handleUseE} style={linkStyle}>
                {t('useEulerNumber')}
              </a>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" onClick={handleClear} style={clearButtonStyle}>
                {t('clearButton')}
              </button>
            </div>

            <div style={errorMessageStyle}>{error}</div>
          </form>

          <div style={resultsStyle}>
            <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('expressionLabel')}</span>
              <span style={resultValueStyle} dangerouslySetInnerHTML={{ __html: calculationResult?.expression || '' }} />
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('resultLabel')}</span>
              <span style={resultValueStyle}>{calculationResult?.finalResult}</span>
            </div>
            {calculationResult?.scientificNotation && (
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('scientificNotationLabel')}</span>
                <span style={resultValueStyle}>{calculationResult.scientificNotation}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <div style={formulaBoxStyle}>
          <div style={formulaStyle}>{t('formulaBasic')}</div>
        </div>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Key Rules */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('rulesTitle')}</h2>
        <div style={examplesGridStyle}>
          <div style={exampleCardStyle}>
            <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>
              {t('ruleMultiplicationTitle')}
            </strong>
            <code style={{ color: colors.textPrimary, fontSize: '0.95rem' }}>
              {t('ruleMultiplicationFormula')}
            </code>
          </div>
          <div style={exampleCardStyle}>
            <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>
              {t('ruleDivisionTitle')}
            </strong>
            <code style={{ color: colors.textPrimary, fontSize: '0.95rem' }}>
              {t('ruleDivisionFormula')}
            </code>
          </div>
          <div style={exampleCardStyle}>
            <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>
              {t('rulePowerTitle')}
            </strong>
            <code style={{ color: colors.textPrimary, fontSize: '0.95rem' }}>
              {t('rulePowerFormula')}
            </code>
          </div>
          <div style={exampleCardStyle}>
            <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>
              {t('ruleNegativeTitle')}
            </strong>
            <code style={{ color: colors.textPrimary, fontSize: '0.95rem' }}>
              {t('ruleNegativeFormula')}
            </code>
          </div>
          <div style={exampleCardStyle}>
            <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>
              {t('ruleZeroTitle')}
            </strong>
            <code style={{ color: colors.textPrimary, fontSize: '0.95rem' }}>
              {t('ruleZeroFormula')}
            </code>
          </div>
          <div style={exampleCardStyle}>
            <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>
              {t('ruleFractionalTitle')}
            </strong>
            <code style={{ color: colors.textPrimary, fontSize: '0.95rem' }}>
              {t('ruleFractionalFormula')}
            </code>
          </div>
        </div>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('insightsBestPracticesTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('insightsPractice1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insightsPractice2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insightsPractice3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insightsPractice4')}</li>
        </ul>
      </section>

      {/* Applications */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('applicationsTitle')}</h2>
        <p style={paragraphStyle}>{t('applicationsParagraph')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('applicationMath')}</strong> {t('applicationMathDesc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('applicationPhysics')}</strong> {t('applicationPhysicsDesc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('applicationEngineering')}</strong> {t('applicationEngineeringDesc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('applicationFinance')}</strong> {t('applicationFinanceDesc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('applicationComputer')}</strong> {t('applicationComputerDesc')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <span style={{ position: 'absolute', left: 0 }}>1.</span>
            <a
              href="https://www.graphcalc.com/exponents-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <span style={{ position: 'absolute', left: 0 }}>2.</span>
            <a
              href="https://study.com/skill/learn/using-a-calculator-to-evaluate-exponential-expressions-explanation.html"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <span style={{ position: 'absolute', left: 0 }}>3.</span>
            <a
              href="https://www.calculator.net/exponent-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <span style={{ position: 'absolute', left: 0 }}>4.</span>
            <a
              href="https://codeyoung.com/blog/what-are-exponents-and-powers-definitions-rules-and-examples-cm8ye0y31001o2k199vl39y0y"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <span style={{ position: 'absolute', left: 0 }}>5.</span>
            <a
              href="https://www.98thpercentile.com/blog/exponents-to-real-world-scenarios/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}